import type { Option } from "replo-runtime/shared/types";

import * as React from "react";

import InputComponent from "@v1/components/common/designSystem/Input";
import useSearcheableOptions from "@v1/hooks/useSearcheableOptions";
import isFunction from "lodash-es/isFunction";
import { Search, X } from "lucide-react";

type SearcheableListOption = Option & {
  content: React.ReactNode | ((option: Option) => React.ReactNode);
};

const SearcheableList: React.FC<{
  title: string;
  options: SearcheableListOption[];
  endEnhancer?: React.ReactNode;
  emptyMessage?: string;
}> = ({ title, options, endEnhancer, emptyMessage }) => {
  const { filteredOptions, searchTerm, setSearchTerm } =
    useSearcheableOptions(options);
  return (
    <div className="flex flex-col gap-4">
      <InputComponent
        size="sm"
        value={searchTerm}
        startEnhancer={<Search size={14} />}
        endEnhancer={() => {
          return (
            searchTerm.trim().length > 0 && (
              <X
                size={12}
                className="text-muted cursor-pointer"
                onClick={() => setSearchTerm("")}
              />
            )
          );
        }}
        placeholder={`Search ${title}`}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <div className="divide-y-0.5 divide-border flex flex-col gap-2">
        {filteredOptions.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {filteredOptions.map(({ content, ...option }) => {
              const contentElement = isFunction(content)
                ? content(option)
                : content;
              return <li key={option.value}>{contentElement}</li>;
            })}
          </ul>
        ) : (
          <div className="text-muted cursor-pointer p-1 text-left text-xs">
            {emptyMessage ?? "No results found"}
          </div>
        )}
        {endEnhancer && <div className="pt-2">{endEnhancer}</div>}
      </div>
    </div>
  );
};

export default SearcheableList;
